import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';
import { GetAppliedNode } from './testUtil';

type Result = ReturnType<typeof useExposureByEnvironment>[0];
export type ExposureResponse = NonNullable<Result['data']>;
export type RequestedExposureAppliedStateNode = GetAppliedNode<
  'exposures',
  ExposureResponse
>;

export const GetExposureByUniqueId = graphql(/* GraphQL */ `
  query Exposure($environmentId: BigInt!, $userId: BigInt, $uniqueIds: [String!]) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        exposures(first: 1, filter: { uniqueIds: $uniqueIds }) {
          edges {
            node {
              fqn
              uniqueId
              url
              tags
              description
              meta
              exposureType
              maturity
              name
              packageName
              filePath
              patchPath
              ownerEmail
              ownerName
              projectId
              manifestGeneratedAt
              autoBiProvider
              integrationId
              freshnessStatus
              quality
              upstreamStats
              maxSnapshottedAt
              healthIssues
              parents {
                uniqueId
                resourceType
                name
                __typename
              }
            }
          }
        }
      }
    }
  }
`);
export const GetExposureAncestorsByUniqueId = graphql(/* GraphQL */ `
  query ExposureAncestors(
    $environmentId: BigInt!
    $userId: BigInt
    $uniqueIds: [String!]
  ) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        exposures(first: 1, filter: { uniqueIds: $uniqueIds }) {
          edges {
            node {
              uniqueId
              ancestors(types: [Model, Source]) {
                uniqueId
                name
                ... on ModelAppliedStateNestedNode {
                  testStatuses
                  executionInfo {
                    lastRunStatus
                  }
                }
                ... on SourceAppliedStateNestedNode {
                  sourceName
                  freshness {
                    freshnessStatus
                    snapshottedAt
                  }
                  testStatuses
                }
              }
            }
          }
        }
      }
    }
  }
`);

type ExposureByEnvironmentParams = {
  environmentId: number | undefined;
  userId?: number | 'loading' | null;
  uniqueId: string;
  pause?: boolean;
};

export const useExposureByEnvironment = ({
  environmentId = 0,
  userId,
  uniqueId,
  pause,
}: ExposureByEnvironmentParams) => {
  const query = useQueryStable({
    query: GetExposureByUniqueId,
    variables: {
      environmentId,
      userId: userId as number | null,
      uniqueIds: [uniqueId],
    },
    pause: pause || !environmentId || !uniqueId || userId === 'loading',
  });

  useCacheMonitor({
    applied: query[0].data?.environment.applied?.lastUpdatedAt,
  });

  return query;
};
export const useExposureAncestorsByEnvironment = ({
  environmentId = 0,
  userId,
  uniqueId,
  pause,
}: ExposureByEnvironmentParams) => {
  const query = useQueryStable({
    query: GetExposureAncestorsByUniqueId,
    variables: {
      environmentId,
      userId: userId as number | null,
      uniqueIds: [uniqueId],
    },
    pause: pause || !environmentId || !uniqueId || userId === 'loading',
  });

  useCacheMonitor({
    applied: query[0].data?.environment.applied?.lastUpdatedAt,
  });

  return query;
};
